import { computed, shallowRef, watch } from "vue";
import { useAsyncData } from "./useAsyncData";
import { getMbSlots, getEscrowSlots } from '../assets/js/utils';
import { toValue } from "../assets/js/vue/utlis/toValue";
import dayjs from "dayjs";
import { isNonWorkingPeriod, isAfter2Weeks, isTodayAndBefore, isDisabledIfHasNoTimeSlots, parseOfficeWorkTimes, } from '../assets/js/scripts/deliveryDateRules';
import { EAppointmentDeliveryType } from "../components-manual/widgets/MeetingForm/const";

/**
 * @enum {string}
 */
export const ERepositoryTimeslots = {
    MB: 'mb',
    APPOINTMENTS: 'appointments',
};

/**
 * @typedef ListState
 * @type {object}
 * @property {import('vue').Ref<object>} valueObject - элемент из списка, соответствующий value
 * @property {(day: Date) => boolean} checkIsDisabledDay - проверяет дату на недоступность
 * @property {() => void} execute - обновить данные вручную
 */

/**
 * Получает опции для селекта (все параметры реактивные)
 * @param {string} filterCityName город (только для repository === mb)
 * @param {string} filterOfficeCode код офиса ифноблока битрикс (только для repository === appointments)
 * @param {string} filterProductId ид продукта (только для repository === mb)
 * @param {string} filterDate дата DD.MM.YYYY
 * @param {object} officeObject объект офиса ифноблока битрикс (только для repository === appointments)
 * @param {ERepositoryTimeslots} repository от куда брать список городов ()
 * @param {number|string} value - значение code для определения valueObject
 * @param {object} options options
 * @param {boolean} options.immediate - опция аналогично useAsyncData
 * @param {boolean} options.disabled - отключить отслеживание параметров и запрос списка
 * @returns {ListState}
 */
export const useBankTimeslotsList = (
    filterCityName,
    filterOfficeCode,
    filterProductId,
    filterDateRaw,
    officeObject,
    repositoryRaw,
    value,
    options = {}
) => {
    const immediate = !toValue(options?.disabled) && options?.immediate || false;
    const filterDate = computed(() => toValue(filterDateRaw));
    const disabled = computed(() => toValue(options?.disabled) ?? false);
    const repository = computed(() => toValue(repositoryRaw));

    const getStartDate = () => dayjs().format('DD.MM.YYYY');
    const getEndDate = () => dayjs().add(14, 'day').format('DD.MM.YYYY');

    const queryMb = computed(() => {
        return { cityName: toValue(filterCityName), productId: toValue(filterProductId), startDate: getStartDate(), endDate: getEndDate() }
    });

    const queryApp = computed(() => {
        return { office: toValue(filterOfficeCode), date: filterDate.value, };
    });

    const noAvailableDays = computed(() => repository.value === ERepositoryTimeslots.MB ? mbAvailableDates.value.length === 0 : false);

    const { pending: pendingMb, data: availableSlotsMb, execute: executeMb, error: errorMb } = useAsyncData(async ({ signal }) => {
        let list = [];

        if (disabled.value) return list;

        const notValidRequest = !queryMb.value.productId || !queryMb.value.cityName || !queryMb.value.startDate || !queryMb.value.endDate;

        if (repository.value !== ERepositoryTimeslots.MB || notValidRequest) return list;
        
        const res = await getMbSlots(queryMb.value, { signal });
        list = res.filter((item) => item.workingDaySlots?.length > 0);

        return list;
    }, { deep: false, watch: [disabled, queryMb, repository], default: () => [], immediate });

    const { pending: pendingApp, data: dataApp, execute: executeApp, error: errorApp } = useAsyncData(async ({ signal }) => {
        let list = [];

        if (disabled.value) return list;

        if (repository.value !== ERepositoryTimeslots.APPOINTMENTS) return list;

        if (!queryApp.value.date || !queryApp.value.office) return list;

        const slots = await getEscrowSlots(queryApp.value, { signal });

        list = slots?.map((raw) => ({
            const: raw.replace(':', '-'),
            title: raw,
            slotType: 'office',
            raw,
        })) ?? [];

        return list;
    }, { deep: false, watch: [disabled, queryApp, filterDate, repository], default: () => [], immediate });

    /** объект с выбранным значением (по const итема из списка) value */
    const valueObject = shallowRef(null);

    /** список доступных дат для МБ */
    const mbAvailableDates = computed(() => {
        return availableSlotsMb.value
            ?.filter((item) => item.workingDaySlots.some((slot) => slot.freeSlotNum > 0))
            .map((item) => dayjs(item.workingDay, 'DD.MM.YYYY')) ?? [];
    });

    const data = computed(() => {
        let list = [];

        if (repository.value === ERepositoryTimeslots.MB) {
            const slots = availableSlotsMb.value
                .find(({ workingDay }) => workingDay === filterDate.value)?.workingDaySlots
                ?.filter((slot) => slot.freeSlotNum > 0)
                ?.map((slot) => ({
                    ...slot,
                    _sortToken: parseInt(`${slot.slotStartTime.replaceAll(':', '')}${slot.slotEndTime.replaceAll(':', '')}`),
                }))
                ?? [];

            slots.sort((a, b) => {
                return a._sortToken.valueOf() - b._sortToken.valueOf();
            }, []);

            const slotsWithoutDouplicates = slots.reduce((acc, cur, i) => {
                if (i && acc[acc.length - 1].slotStartTime === cur.slotStartTime && acc[acc.length - 1].slotEndTime === cur.slotEndTime) {
                    acc[acc.length - 1].slotType = 'MB';
                } else {
                    acc.push(cur);
                }
                return acc;
            }, []);

            list = slotsWithoutDouplicates?.map((raw) => ({
                const: `${raw.slotStartTime}-${raw.slotEndTime}`,
                title: `с ${raw.slotStartTime.slice(0, 5)} до ${raw.slotEndTime.slice(0, 5)}`,
                slotType: raw.slotType === 'MB' ? EAppointmentDeliveryType.MB : EAppointmentDeliveryType.COURIER,
                raw,
            })) ?? [];
        } else {
            list = dataApp.value
        }

        return list;
    });
    const pending = computed(() => repository.value === ERepositoryTimeslots.MB ? pendingMb.value : pendingApp.value);
    const execute = computed(() => repository.value === ERepositoryTimeslots.MB ? executeMb : executeApp);
    const error = computed(() => repository.value === ERepositoryTimeslots.MB ? errorMb.value : errorApp.value);

    watch([data, () => toValue(value)], ([data, value]) => {
        if (!value || data.length === 0) {
            valueObject.value = null;
            return;
        }
        valueObject.value = data.find((item) => item.const === value) ?? null;
    });

    /**
     * проверяет дату на недоступность
     * @param {Date} val
     * @returns {boolean}
     */
    const checkIsDisabledDay = (val) => {
        let isDisabled = false;

        if (repository.value === ERepositoryTimeslots.APPOINTMENTS) {

            const officeCode = Number.parseInt(toValue(filterOfficeCode));

            const officeRawTimeslots = parseOfficeWorkTimes(officeCode, [toValue(officeObject)]);

            isDisabled = isTodayAndBefore(val) || isNonWorkingPeriod(val, officeCode, [toValue(officeObject)]) || isAfter2Weeks(val) || isDisabledIfHasNoTimeSlots(val, officeRawTimeslots);
        } else {
            isDisabled = !mbAvailableDates.value.some((availableDate) => dayjs(val).isSame(availableDate, 'day'))
        }

        return isDisabled;
    };

    return {
        data,
        pending,
        execute,
        error,
        valueObject,
        checkIsDisabledDay,
        noAvailableDays,
    };
};
